import React from "react";

import {
  DropzoneInputProps,
  DropzoneRootProps,
  FileRejection,
  useDropzone,
} from "react-dropzone";

import { humanReadableSize } from "src/utils/files";
import { logger } from "src/utils/logger";

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB

type Props = {
  maxFiles?: number;
  onSubmitFiles(files: File[], fileErrors: string[]): Promise<void>;
  content({
    getRootProps,
    getInputProps,
  }: {
    getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
    getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  }): JSX.Element;
};

export const DecideDropzone: React.FC<Props> = ({
  onSubmitFiles,
  content,
  maxFiles,
}) => {
  const handleOnDrop = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
  ) => {
    const fileErrors = rejectedFiles.map(
      (fileRejection) =>
        `${fileRejection.file.name}: ${
          fileRejection.errors[0]?.code === "file-too-large"
            ? `File is larger than ${humanReadableSize(MAX_FILE_SIZE_BYTES)}`
            : fileRejection.errors[0]?.message
        }`,
    );
    onSubmitFiles(acceptedFiles, fileErrors);
    rejectedFiles.forEach((rejection) => {
      logger.error({
        message: rejection.errors[0]?.message,
        placement: "top",
        className: "bg-red-100",
      });
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleOnDrop,
    accept: {
      "text/csv": [".csv"],
      "application/json": [".json"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    useFsAccessApi: false,
    maxFiles: maxFiles,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  return content({ getRootProps, getInputProps });
};
